import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '@shs-digi-care-monorepo/frontend-lib';
import { filter, map, Observable, of, tap } from 'rxjs';
import { OpenRequestTab } from './open-request-tabs.model';

/**
 * Holds the request "tabs" the user has opened while working through the task
 * lists. The tabs are shared between the task overview (where they are rendered
 * inline) and the sidebar (where they are listed below the Requests item) and
 * are persisted to {@link Window.localStorage} so they are reopened when the
 * user comes back to the application, even after closing the browser.
 */
@Injectable({ providedIn: 'root' })
export class OpenRequestTabsService {
    private static readonly STORAGE_KEY = 'mdla:open-request-tabs';

    private readonly dialog = inject(MatDialog);

    private readonly _tabs = signal<OpenRequestTab[]>(this.restore());
    private readonly _activeId = signal<string | null>(null);
    private readonly _dirtyIds = signal<ReadonlySet<string>>(new Set());

    public readonly tabs: Signal<OpenRequestTab[]> = this._tabs.asReadonly();
    public readonly activeId: Signal<string | null> = this._activeId.asReadonly();
    /** Ids of the request tabs that hold unsaved changes. */
    public readonly dirtyIds: Signal<ReadonlySet<string>> = this._dirtyIds.asReadonly();

    /** The currently open request tab, or `null` when no request tab is active. */
    public readonly activeTab = computed(() => this._tabs().find((tab) => tab.id === this._activeId()) ?? null);

    constructor() {
        // Lets the browser show its "leave site?" prompt when closing or reloading with unsaved edits.
        const onBeforeUnload = (event: BeforeUnloadEvent): void => {
            if (this._dirtyIds().size > 0) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        inject(DestroyRef).onDestroy(() => window.removeEventListener('beforeunload', onBeforeUnload));
    }

    /**
     * Opens a tab for the given request and makes it the active one. An already open
     * request is always focused instead of being opened twice; a new tab opened with
     * `background` is added without leaving the current view.
     */
    public open(tab: OpenRequestTab, { background = false }: { background?: boolean } = {}): void {
        if (this._tabs().some((existing) => existing.id === tab.id)) {
            this._activeId.set(tab.id);
            return;
        }
        this._tabs.update((tabs) => [...tabs, tab]);
        this.persist();
        if (!background) {
            this._activeId.set(tab.id);
        }
    }

    /** Marks the tab with the given id as active, or clears the active tab with `null`. */
    public setActive(id: string | null): void {
        this._activeId.set(id);
    }

    /** Updates the tab label information once it becomes known. */
    public updateLabel(id: string, displayId: string, subject?: string): void {
        if (!displayId) {
            return;
        }
        let changed = false;
        this._tabs.update((tabs) =>
            tabs.map((tab) => {
                if (tab.id === id && (tab.displayId !== displayId || tab.subject !== subject)) {
                    changed = true;
                    return { ...tab, displayId, subject };
                }
                return tab;
            })
        );
        if (changed) {
            this.persist();
        }
    }

    public setDirty(id: string, dirty: boolean): void {
        if (this._dirtyIds().has(id) === dirty) {
            return;
        }
        this._dirtyIds.update((ids) => {
            const next = new Set(ids);
            if (dirty) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }

    /** Closes the tab for the given request id, clearing the active tab if it was active. */
    public close(id: string): void {
        this.remove([id]);
    }

    /**
     * Closes the tabs for the given request ids once the user confirmed discarding their unsaved changes.
     * The active tab stays active; if it is among the closed ones, `fallbackId` becomes active instead.
     */
    public closeWithConfirmation(ids: string[], fallbackId: string | null = null): void {
        if (ids.length === 0) {
            return;
        }
        this.confirmDiscard(ids)
            .pipe(
                filter(Boolean),
                tap(() => this.remove(ids, fallbackId))
            )
            .subscribe();
    }

    /**
     * Resolves to `true` right away when none of the given tabs holds unsaved changes,
     * otherwise asks the user whether these changes may be discarded.
     */
    public confirmDiscard(ids: string[] = this._tabs().map((tab) => tab.id)): Observable<boolean> {
        if (!ids.some((id) => this._dirtyIds().has(id))) {
            return of(true);
        }
        return this.dialog
            .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
                autoFocus: false,
                data: {
                    title: 'common.discard-changes-dialog.title',
                    message: 'common.discard-changes-dialog.description',
                    confirmLabel: 'common.discard-changes-dialog.confirm',
                    cancelLabel: 'common.discard-changes-dialog.cancel',
                    confirmTestId: 'emea-request-tabs-discard-confirm'
                }
            })
            .afterClosed()
            .pipe(map(Boolean));
    }

    private remove(ids: string[], fallbackId: string | null = null): void {
        this._tabs.update((tabs) => tabs.filter((tab) => !ids.includes(tab.id)));
        this._dirtyIds.update((dirtyIds) => new Set([...dirtyIds].filter((id) => !ids.includes(id))));
        if (ids.includes(this._activeId() ?? '')) {
            this._activeId.set(fallbackId);
        }
        this.persist();
    }

    private persist(): void {
        const storage = this.getStorage();
        if (!storage) {
            return;
        }
        try {
            storage.setItem(OpenRequestTabsService.STORAGE_KEY, JSON.stringify(this._tabs()));
        } catch {
            // Ignore storage write failures (e.g. quota or sandboxed iframes).
        }
    }

    private restore(): OpenRequestTab[] {
        const storage = this.getStorage();
        if (!storage) {
            return [];
        }
        try {
            const saved = storage.getItem(OpenRequestTabsService.STORAGE_KEY) ?? this.takeLegacySessionTabs(storage);
            if (!saved) {
                return [];
            }
            const parsed: unknown = JSON.parse(saved);
            if (!Array.isArray(parsed)) {
                return [];
            }
            // The API delivers `displayId` as a number although the model types it as a string.
            return parsed
                .filter(
                    (tab) =>
                        typeof tab?.id === 'string' &&
                        (typeof tab?.displayId === 'string' || typeof tab?.displayId === 'number')
                )
                .map((tab) => ({ ...tab, displayId: String(tab.displayId) }));
        } catch {
            return [];
        }
    }

    /** Moves tabs saved by earlier versions, which kept them in sessionStorage, over to localStorage. */
    private takeLegacySessionTabs(storage: Storage): string | null {
        const legacy = window.sessionStorage?.getItem(OpenRequestTabsService.STORAGE_KEY) ?? null;
        if (legacy) {
            storage.setItem(OpenRequestTabsService.STORAGE_KEY, legacy);
            window.sessionStorage.removeItem(OpenRequestTabsService.STORAGE_KEY);
        }
        return legacy;
    }

    private getStorage(): Storage | null {
        try {
            if (typeof window === 'undefined' || !window.localStorage) {
                return null;
            }
            return window.localStorage;
        } catch {
            // Accessing localStorage can throw (e.g. sandboxed iframes or
            // privacy settings); treat it as unavailable.
            return null;
        }
    }
}
